/* eslint-disable max-len */
import Button from 'components/Basic/Button';
import { useDispatch, useSelector } from 'react-redux';
import { toggleDebugDialogState } from 'store/slices/DebugDialogSlice';
import { toPng } from 'html-to-image';
import { useState } from 'react';
import { RootState } from 'store';

interface CaptureResult {
  imgbase64String?: string;
  error?: Error;
}

const waitForAnimationsToSettle = async (): Promise<void> => {
	const animations = document.body.getAnimations({ subtree: true })
		.filter(animation => animation.effect?.getComputedTiming().iterations !== Infinity);
	await Promise.all(animations.map(animation => animation.finished.catch(() => undefined)));
};

const syncCheckedAttributesForCapture = (): (() => void) => {
	const inputs = Array.from(document.body.querySelectorAll<HTMLInputElement>('input[type="checkbox"], input[type="radio"]'));
	const hadCheckedAttr = inputs.map(input => input.hasAttribute('checked'));

	inputs.forEach(input => {
		if (input.checked) input.setAttribute('checked', '');
		else input.removeAttribute('checked');
	});

	return () => {
		inputs.forEach((input, index) => {
			if (hadCheckedAttr[index]) input.setAttribute('checked', '');
			else input.removeAttribute('checked');
		});
	};
};

export default function GetDebugDataBtn(){
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(false);
	const { hideSatelliteOverlay } = useSelector((state: RootState) => state.toolNewPostions.data);

	const captureScreenshot = async (): Promise<CaptureResult> => {
		await waitForAnimationsToSettle();
		const restoreCheckedAttributes = syncCheckedAttributesForCapture();
		try {
			const img = await toPng(document.body, {
				filter: node => node.tagName?.toLowerCase() !== 'noscript',
			});
			return {
				imgbase64String: img,
			};
		} catch (err) {
			const error = err instanceof Error ? err : new Error(String(err));
			console.error('Error capturing screenshot:', error);
			return {
				error,
			};
		} finally {
			restoreCheckedAttributes();
		}
	};

	const onClickHandler = async () => {
		setLoading(true);
		const { imgbase64String, error } = await captureScreenshot();
		window.parent.postMessage(
			{ id: 'GET_THE_PROPOSAL_URL' },
			'*'
		);
		setLoading(false);
		dispatch(
			toggleDebugDialogState({
				value: true,
				screenshotImage: imgbase64String,
				errorInCapturingScreenshot: error
					? error.message
					: undefined,
			})
		);
	};
	return (
		<Button
			id="debug-btn"
			className='debug-btn btnHover'
			tooltipContent='Upload & download debug info'
			onClick={onClickHandler}
			showLoader={!!loading}
			disabled={hideSatelliteOverlay}>
			<svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path d="M5.80078 1L7.30478 2.504M10.6968 2.504L12.2008 1M6.60078 5.104V4.304C6.58628 3.9798 6.63761 3.65602 6.75168 3.3522C6.86575 3.04838 7.0402 2.77083 7.26449 2.53628C7.48877 2.30173 7.75826 2.11506 8.05668 1.98753C8.35509 1.85999 8.67625 1.79424 9.00078 1.79424C9.32531 1.79424 9.64647 1.85999 9.94489 1.98753C10.2433 2.11506 10.5128 2.30173 10.7371 2.53628C10.9614 2.77083 11.1358 3.04838 11.2499 3.3522C11.364 3.65602 11.4153 3.9798 11.4008 4.304V5.104" stroke="var(--defaultIconColor)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
				<path d="M8.99922 15.4C6.35922 15.4 4.19922 13.24 4.19922 10.6V8.2C4.19922 7.35131 4.53636 6.53737 5.13648 5.93726C5.73659 5.33714 6.55053 5 7.39922 5H10.5992C11.4479 5 12.2618 5.33714 12.862 5.93726C13.4621 6.53737 13.7992 7.35131 13.7992 8.2V10.6C13.7992 13.24 11.6392 15.4 8.99922 15.4ZM8.99922 15.4V8.2" stroke="var(--defaultIconColor)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
				<path d="M4.624 6.5999C3.08 6.4399 1.8 5.0799 1.8 3.3999M4.2 9.7999H1M1.8 16.1999C1.8 14.5199 3.16 13.0799 4.84 12.9999M16.176 3.3999C16.176 5.0799 14.896 6.4399 13.376 6.5999M17 9.7999H13.8M13.16 12.9999C14.84 13.0799 16.2 14.5199 16.2 16.1999" stroke="var(--defaultIconColor)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
			</svg>
		</Button>
	);
}